import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Send, MessageSquare, CheckCircle, XCircle, Clock, RefreshCw, Edit, FileText, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsTab = () => {
  const [statistics, setStatistics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('messages'); // 'messages' | 'templates'
  
  const [messageForm, setMessageForm] = useState({
    to_number: '',
    message_body: '',
    message_type: 'manual'
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    message_type: 'manual',
    template_body: ''
  });

  // Verileri çek
  useEffect(() => {
    fetchStatistics();
    fetchMessages();
    fetchTemplates();
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/sms/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error('İstatistikler alınamadı:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/sms/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Mesajlar alınamadı:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/sms/templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Şablonlar alınamadı:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.to_number || !messageForm.message_body) {
      toast.error('Telefon numarası ve mesaj gerekli');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/sms/send`, messageForm);
      toast.success('WhatsApp mesajı gönderildi!');
      setShowSendModal(false);
      setMessageForm({ to_number: '', message_body: '', message_type: 'manual' });
      fetchStatistics();
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'WhatsApp gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.name || !templateForm.template_body) {
      toast.error('Şablon adı ve içerik gerekli');
      return;
    }

    try {
      await axios.post(`${API}/sms/templates`, templateForm);
      toast.success('Şablon oluşturuldu');
      setShowTemplateModal(false);
      setTemplateForm({ name: '', message_type: 'manual', template_body: '' });
      fetchTemplates();
    } catch (error) {
      toast.error('Şablon oluşturulamadı');
    }
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    if (!editingTemplate?.template_body) {
      toast.error('Şablon içeriği gerekli');
      return;
    }

    try {
      await axios.put(`${API}/sms/templates/${editingTemplate.id}`, {
        template_body: editingTemplate.template_body,
        is_active: editingTemplate.is_active
      });
      toast.success('Şablon güncellendi');
      setShowTemplateModal(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error('Şablon güncellenemedi');
    }
  };

  const handleToggleTemplate = async (template) => {
    try {
      await axios.put(`${API}/sms/templates/${template.id}`, {
        is_active: !template.is_active
      });
      toast.success(`Şablon ${!template.is_active ? 'aktif edildi' : 'devre dışı bırakıldı'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const openEditTemplate = (template) => {
    setEditingTemplate({...template});
    setShowTemplateModal(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      sent: { color: 'bg-green-100 text-green-700', icon: <CheckCircle className="w-3 h-3" />, text: 'Gönderildi' },
      failed: { color: 'bg-red-100 text-red-700', icon: <XCircle className="w-3 h-3" />, text: 'Başarısız' },
      queued: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock className="w-3 h-3" />, text: 'Kuyrukta' }
    };
    const variant = variants[status] || variants.queued;
    return (
      <Badge className={`${variant.color} flex items-center gap-1`}>
        {variant.icon}
        {variant.text}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    const labels = {
      reservation_confirmation: 'Rezervasyon Onayı',
      reservation_reminder: 'Rezervasyon Hatırlatma',
      payment_reminder: 'Ödeme Hatırlatma',
      referee_assignment: 'Hakem Ataması',
      referee_report_reminder: 'Hakem Rapor Hatırlatma',
      feedback_request: 'Maç Sonrası Geri Bildirim',
      personnel_task_assignment: 'Personel Görev Atama',
      personnel_task_completed: 'Görev Tamamlandı (Yöneticiye)',
      manual: 'Manuel Mesaj'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4">
      {/* Başlık */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800">WhatsApp Bildirimleri</h2>
          <p className="text-sm text-slate-500">wamessage.app ile otomatik ve manuel WhatsApp yönetimi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchStatistics(); fetchMessages(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowSendModal(true)} className="bg-green-600 hover:bg-green-700">
            <Send className="w-4 h-4 mr-1" /> WhatsApp Gönder
          </Button>
        </div>
      </div>

      {/* İstatistik Kartları */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-xs text-blue-600 font-medium">Toplam Mesaj</p>
              <p className="text-2xl font-bold text-blue-700">{statistics.total_messages}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <p className="text-xs text-green-600 font-medium">Gönderildi</p>
              <p className="text-2xl font-bold text-green-700">{statistics.sent_messages}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4">
              <p className="text-xs text-red-600 font-medium">Başarısız</p>
              <p className="text-2xl font-bold text-red-700">{statistics.failed_messages}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4">
              <p className="text-xs text-purple-600 font-medium">Başarı Oranı</p>
              <p className="text-2xl font-bold text-purple-700">{statistics.success_rate}%</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4">
              <p className="text-xs text-orange-600 font-medium">Bugün</p>
              <p className="text-2xl font-bold text-orange-700">{statistics.today_messages}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Görünüm Seçici */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-lg w-fit">
        <Button
          variant={activeView === 'messages' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('messages')}
          className={activeView === 'messages' ? 'bg-white shadow' : ''}
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          WhatsApp Mesajları
        </Button>
        <Button
          variant={activeView === 'templates' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setActiveView('templates')}
          className={activeView === 'templates' ? 'bg-white shadow' : ''}
        >
          <FileText className="w-4 h-4 mr-1" />
          WhatsApp Şablonları
        </Button>
      </div>

      {/* SMS Listesi */}
      {activeView === 'messages' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Gönderilen WhatsApp Mesajları</CardTitle>
          </CardHeader>
          <CardContent>
            {messages.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Henüz mesaj gönderilmemiş</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {messages.map(msg => (
                  <div key={msg.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{msg.to_number}</p>
                        {getStatusBadge(msg.status)}
                        <Badge variant="outline" className="text-xs">{getTypeLabel(msg.message_type)}</Badge>
                      </div>
                      <span className="text-xs text-slate-500">{new Date(msg.sent_at).toLocaleString('tr-TR')}</span>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{msg.message_body}</p>
                    {msg.error_message && (
                      <p className="text-xs text-red-500 mt-1">Hata: {msg.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Şablon Listesi */}
      {activeView === 'templates' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Otomatik WhatsApp Şablonları</CardTitle>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Henüz şablon yok</p>
              </div>
            ) : (
              <div className="space-y-3">
                {templates.map(template => (
                  <div key={template.id} className={`p-4 rounded-lg border-2 ${template.is_active ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-800">{template.name}</h3>
                        <Badge variant="outline" className="text-xs">{getTypeLabel(template.message_type)}</Badge>
                        {template.is_active ? (
                          <Badge className="bg-green-500 text-white text-xs">Aktif</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-slate-500">Devre Dışı</Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => handleToggleTemplate(template)}>
                          {template.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditTemplate(template)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white p-3 rounded border border-slate-200">
                      <p className="text-sm text-slate-600 whitespace-pre-wrap font-mono">{template.template_body}</p>
                    </div>
                    {template.variables && template.variables.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-slate-500">Değişkenler:</span>
                        {template.variables.map((variable, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {`{${variable}}`}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* SMS Gönder Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>WhatsApp Gönder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div>
              <Label>Telefon Numarası *</Label>
              <Input
                placeholder="05XX XXX XX XX"
                value={messageForm.to_number}
                onChange={(e) => setMessageForm({...messageForm, to_number: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">Türkiye için 0 ile başlayabilir (örn: 05321234567)</p>
            </div>
            <div>
              <Label>Mesaj *</Label>
              <Textarea
                placeholder="WhatsApp mesajınızı yazın..."
                value={messageForm.message_body}
                onChange={(e) => setMessageForm({...messageForm, message_body: e.target.value})}
                rows={6}
              />
              <p className="text-xs text-slate-500 mt-1">{messageForm.message_body.length} karakter</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowSendModal(false)} className="flex-1">
                İptal
              </Button>
              <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                {loading ? 'Gönderiliyor...' : 'Gönder'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Şablon Düzenleme Modal */}
      <Dialog open={showTemplateModal} onOpenChange={(open) => {
        setShowTemplateModal(open);
        if (!open) setEditingTemplate(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Şablon Düzenle' : 'Yeni Şablon Oluştur'}</DialogTitle>
          </DialogHeader>
          {editingTemplate ? (
            <form onSubmit={handleUpdateTemplate} className="space-y-4">
              <div>
                <Label>Şablon Adı</Label>
                <Input value={editingTemplate.name || ''} disabled className="bg-slate-50" />
              </div>
              <div>
                <Label>Şablon Türü</Label>
                <Input value={getTypeLabel(editingTemplate.message_type)} disabled className="bg-slate-50" />
              </div>
              <div>
                <Label>Mesaj İçeriği *</Label>
                <Textarea
                  value={editingTemplate.template_body || ''}
                  onChange={(e) => setEditingTemplate({...editingTemplate, template_body: e.target.value})}
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500 mt-1">{editingTemplate.template_body?.length || 0} karakter</p>
              </div>
              {editingTemplate.variables && editingTemplate.variables.length > 0 && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 mb-2">Kullanılabilir Değişkenler:</p>
                  <div className="flex flex-wrap gap-2">
                    {editingTemplate.variables.map((variable, idx) => (
                      <code key={idx} className="px-2 py-1 bg-white rounded border border-blue-200 text-blue-700 text-xs">
                        {`{${variable}}`}
                      </code>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 mt-2">
                    💡 Bu değişkenler otomatik olarak gerçek verilerle değiştirilir
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  id="template-active"
                  checked={editingTemplate.is_active || false}
                  onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <Label htmlFor="template-active" className="cursor-pointer">
                  Bu şablonu aktif et (otomatik mesaj gönderiminde kullanılsın)
                </Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowTemplateModal(false); setEditingTemplate(null); }} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Güncelle
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div>
                <Label>Şablon Adı *</Label>
                <Input
                  placeholder="Örn: Özel Rezervasyon Mesajı"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Mesaj İçeriği *</Label>
                <Textarea
                  placeholder="Mesaj şablonunuzu yazın..."
                  value={templateForm.template_body}
                  onChange={(e) => setTemplateForm({...templateForm, template_body: e.target.value})}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowTemplateModal(false)} className="flex-1">
                  İptal
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Oluştur
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationsTab;
